import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/common/logo';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10">
          {/* Back Button */}
          <div className="mb-4">
            <Link
              to="/"
              className="inline-flex items-center text-primary hover:text-primary-dark font-medium transition-colors hover:underline"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              Back to Home
            </Link>
          </div>

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <Logo size="default" />
              <h1 className="text-2xl sm:text-3xl font-bold text-[#8B4513]">Pangolin Script</h1>
            </Link>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Effective Date: January 2, 2026</p>

          {/* Content */}
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              Pangolin ("we," "our," or "us") respects your privacy. This Privacy Policy explains how we collect, use, and share information when you use our application ("App") that interacts with the YouTube API. By using Pangolin, you agree to the practices described below.
            </p>

            <hr className="my-8 border-gray-200" />

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Public YouTube Data</h3>
            <p className="text-gray-700 mb-2">Pangolin accesses publicly available information from YouTube via the YouTube API, including:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
              <li>Video titles and descriptions</li>
              <li>View counts</li>
              <li>Likes</li>
              <li>Creator information</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Google User Data</h3>
            <p className="text-gray-700 mb-2">
              When you sign in with Google OAuth, we access the following Google user data:
            </p>
            <ul className="list-disc pl-6 mb-2 text-gray-700 space-y-1">
              <li>Your email address (for authentication and account creation)</li>
              <li>Your profile information (name and profile picture)</li>
              <li>Public YouTube videos from your channel (to help generate scripts)</li>
            </ul>
            <p className="text-gray-700 mb-4">
              This data is used solely for authentication purposes and to provide the core functionality of script generation. We do not share, sell, or use this data for any other purpose.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Your Content</h3>
            <p className="text-gray-700 mb-4">
              If you provide your YouTube account information, the App may access your public YouTube videos to help generate scripts.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">What We Do NOT Collect</h3>
            <p className="text-gray-700 mb-2">We do not collect:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
              <li>Your YouTube password or private account information</li>
              <li>Private videos or private user data</li>
              <li>Sensitive personal information (e.g., financial, health, or government ID data)</li>
            </ul>

            <hr className="my-8 border-gray-200" />

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Information</h2>
            <p className="text-gray-700 mb-2">We use the collected information to:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
              <li>Retrieve and display publicly available YouTube content</li>
              <li>Generate scripts, summaries, and insights for YouTube videos</li>
              <li>Improve and maintain the functionality of the App</li>
            </ul>

            <hr className="my-8 border-gray-200" />

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Data Sharing</h2>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
              <li>We do not sell, trade, or rent your information.</li>
              <li>Information accessed from YouTube is used in compliance with the YouTube API Terms of Service.</li>
              <li>Third-party services (e.g., analytics or crash reporting) may receive anonymized usage data.</li>
            </ul>

            <hr className="my-8 border-gray-200" />

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Third-Party Services</h2>
            <p className="text-gray-700 mb-2">Our App relies on the YouTube API and is subject to Google's policies, including:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
              <li>
                <a
                  href="https://www.youtube.com/t/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-dark hover:underline"
                >
                  YouTube Terms of Service
                </a>
              </li>
              <li>
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-dark hover:underline"
                >
                  Google Privacy Policy
                </a>
              </li>
            </ul>
            <p className="text-gray-700 mb-4">
              By using Pangolin, you agree to these third-party terms as applicable.
            </p>

            <hr className="my-8 border-gray-200" />

            {/* Section 5 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-700 mb-4">
              We implement reasonable administrative, technical, and physical safeguards to protect the data collected. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.
            </p>

            <hr className="my-8 border-gray-200" />

            {/* Section 6 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Retention and Deletion</h2>
            <p className="text-gray-700 mb-4">
              We retain Google user data (e.g., email address, profile information, public YouTube videos from your channel) only as long as necessary to provide App functionality, authentication, and script generation—typically for the duration of your account's active use.
            </p>
            <p className="text-gray-700 mb-4">
              Data is securely deleted or anonymized upon account deletion, user request, or when no longer needed (e.g., within 30 days of inactivity). You may request deletion by emailing{' '}
              <a
                href="mailto:shahzaibfarooq582@gmail.com"
                className="text-primary hover:text-primary-dark hover:underline"
              >
                shahzaibfarooq582@gmail.com
              </a>
              . When the retention period expires, we delete or destroy the data.
            </p>

            <hr className="my-8 border-gray-200" />

            {/* Section 7 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Children's Privacy</h2>
            <p className="text-gray-700 mb-4">
              Pangolin is not directed to children under 13, and we do not knowingly collect information from children.
            </p>

            <hr className="my-8 border-gray-200" />

            {/* Section 7 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 mb-4">
              We may update this Privacy Policy from time to time. Any changes will be reflected on this page with an updated effective date. Your continued use of Pangolin constitutes acceptance of any changes.
            </p>

            <hr className="my-8 border-gray-200" />

            {/* Section 8 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Contact Us</h2>
            <p className="text-gray-700 mb-2">
              For questions or concerns about this Privacy Policy, please contact us at:
            </p>
            <p className="text-gray-700">
              Email:{' '}
              <a
                href="mailto:shahzaibfarooq582@gmail.com"
                className="text-primary hover:text-primary-dark hover:underline"
              >
                shahzaibfarooq582@gmail.com
              </a>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} PangolinScript. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
